
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { GetStaticPropsResult } from 'next';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { useLocalStorage } from 'react-use';
import { Product } from 'types';
import { getActiveProductsWithPrices } from '@/data/products';
// Above-the-fold: imported statically — the hero headline is the LCP element.
import HomeHero from '@/components/home/HomeHero';
import Footer from '@/components/layout/Footer';
import Seo from '@/components/Seo';
// Below-the-fold sections are code-split.
const ShowcaseMarquee = dynamic(() => import('@/components/home/ShowcaseMarquee'));
const UseCaseCards = dynamic(() => import('@/components/home/UseCaseCards'));
const ModelsBand = dynamic(() => import('@/components/home/ModelsBand'));
const ToolsShowcase = dynamic(() => import('@/components/home/ToolsShowcase'));
const TrustSection = dynamic(() => import('@/components/home/TrustSection'));
const Testimonials = dynamic(() => import('@/components/Testimonials'), {
    ssr: false,
});
const PricingForLandingPage = dynamic(() => import('@/components/PricingForLandingPage'));
const Faq = dynamic(() => import('@/components/Faq'));
const FinalCta = dynamic(() => import('@/components/home/FinalCta'));
const FeaturedLogos = dynamic(() => import('@/components/FeaturedLogos'));
// Only referral visitors pay for this chunk — it's rendered conditionally.
const ReferralWelcomeModal = dynamic(() => import('@/components/ReferralWelcomeModal'), { ssr: false });
import useLocale from 'next-translate/useTranslation';
import { HAS_FREE_PLAN } from '@/constants/common';
interface Props {
    products: Product[];
    isStaticPage?: boolean;
}
export default function HomePage({ products }: Props) {
    const router = useRouter();
    const { from } = router.query;
    const { t } = useLocale('home');
    const [showStickyCTA, setShowStickyCTA] = useState(false);
    const [showReferralModal, setShowReferralModal] = useState(false);
    const [storedFrom, setFromValue] = useLocalStorage('from', from);
    // A referral is active when the visitor arrives via ?from=<inviter id> or
    // has one saved from a previous visit (cleared after signup on /dashboard).
    const hasReferral = Boolean(from || storedFrom);
    const filteredProducts = useMemo(() => products.filter((plan) => HAS_FREE_PLAN || plan.name !== 'Free'), [products]);
    useEffect(() => {
        if (from) {
            setFromValue(from);
            setShowReferralModal(true);
        }
    }, [from, setFromValue]);
    // The contact form moved to /contact; keep old `/#contact-us` links
    // (emails, external sites, cached legal pages) landing on the form.
    useEffect(() => {
        if (window.location.hash === '#contact-us') {
            router.replace('/contact');
        }
    }, [router]);
    // Show sticky CTA after scrolling past hero section
    useEffect(() => {
        const handleScroll = () => {
            setShowStickyCTA(window.scrollY > 700);
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);
    return (<main className='relative min-h-screen bg-[#07070C]'>
      <Seo templateTitle={t('All-In-One AI Platform')} title={t('All-In-One AI Platform')} description={t('hero_text')} image='https://media.easy-peasy.ai/landing/home/og-home-v3.jpg' products={products} page='home' productName={t('All-In-One AI Platform')} productDescription={t('hero_text')}/>
      {/* Preload the first visible hero-wall tiles — they paint above the fold */}
      <Head>
        <link rel='preload' href='https://media.easy-peasy.ai/landing/home/img-fashion.webp' as='image' type='image/webp' fetchPriority='high'/>
        <link rel='preload' href='https://media.easy-peasy.ai/landing/home/img-anime.webp' as='image' type='image/webp' fetchPriority='high'/>
      </Head>

      {/* Story arc: promise (hero) → proof (marquee) → desire (use cases) →
            capability (tools) → value (models = one subscription) → social
            proof (testimonials + press) → trust (ownership/security) → the ask
            (pricing) → objections (FAQ) → human path (contact) → close. */}
      <HomeHero />
      <ShowcaseMarquee />
      <UseCaseCards />
      <ToolsShowcase />
      <ModelsBand />

      <Testimonials forceDark={true}/>
      <FeaturedLogos forceDark={true} featuredPublication='e27'/>
      <TrustSection />

      <PricingForLandingPage isUpgrade={true} hideLowerPrices={false} products={filteredProducts} showReferralBonus={hasReferral}/>

      {hasReferral && (<ReferralWelcomeModal showModal={showReferralModal} onClose={() => setShowReferralModal(false)} inviterId={typeof from === 'string' ? from : undefined}/>)}
      <div className='dark'>
        <Faq />
        <FinalCta />
        <Footer variant='dark'/>
      </div>

      {/* Sticky mobile CTA bar */}
      <div className={`fixed bottom-0 left-0 right-0 z-50 md:hidden transition-transform ease-drawer motion-reduce:transition-none ${showStickyCTA ? 'translate-y-0 duration-300' : 'translate-y-full duration-200'}`}>
        <div className='flex items-center justify-between gap-3 border-t border-white/10 bg-[#07070C]/95 px-4 py-3 backdrop-blur-lg'>
          <div className='text-sm font-medium text-white'>
            {t('Start creating with AI')}
          </div>
          <Link href='/login#auth-sign-up' className='shrink-0 rounded-full bg-indigo-500 px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-indigo-500/25'>
            {t('Get started free')}
          </Link>
        </div>
      </div>
    </main>);
}
async function getStaticProps(): Promise<GetStaticPropsResult<Props>> {
    const products = await getActiveProductsWithPrices();
    return {
        props: {
            products,
            // Enable lightweight _app.tsx render path that skips MyUserContextProvider
            isStaticPage: true,
        },
        revalidate: 60,
    };
}

    async function __Next_Translate__getStaticProps__1a1a51ddd4f__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/index',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a1a51ddd4f__ as getStaticProps }
  